import { useState } from 'react';
import { useNavigate, useLocation, Outlet, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LayoutDashboard, FolderPlus, History, CreditCard, User, LogOut, Menu, X, Zap, Link2 } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_video-forge-417/artifacts/8amlg66h_Logo%20futuriste%20de%20ViralSynth.com.png";

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/projects/new', label: 'Nouveau Projet', icon: FolderPlus },
  { path: '/history', label: 'Historique', icon: History },
  { path: '/connections', label: 'Comptes connectes', icon: Link2 },
  { path: '/pricing', label: 'Abonnement', icon: CreditCard },
  { path: '/profile', label: 'Profil', icon: User },
];

export default function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const SidebarContent = () => (
    <>
      <div className="p-6 border-b border-white/5">
        <Link to="/dashboard" className="flex items-center gap-2" data-testid="sidebar-logo">
          <img src={LOGO_URL} alt="ViralSynth" className="w-44 h-auto" />
        </Link>
      </div>
      <nav className="flex-1 p-4 space-y-1">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path || (item.path === '/dashboard' && location.pathname.startsWith('/projects/') && item.path === '/dashboard');
          const Icon = item.icon;
          return (
            <button
              key={item.path}
              onClick={() => { navigate(item.path); setSidebarOpen(false); }}
              data-testid={`nav-${item.path.replace(/\//g, '-').slice(1)}`}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-all duration-200 ${
                isActive
                  ? 'bg-white/10 text-white font-medium'
                  : 'text-white/50 hover:text-white hover:bg-white/5'
              }`}
            >
              <Icon className="w-4 h-4" />
              {item.label}
            </button>
          );
        })}
      </nav>
      <div className="p-4 border-t border-white/5">
        <div className="px-4 py-2 mb-3">
          <p className="text-xs text-white/40 truncate">{user?.email}</p>
          <p className="text-xs text-[#FF3300] mt-1">
            {user?.plan_type === 'unlimited' ? '∞ Illimite (VIP)' : `${user?.credits_balance || 0} credits`}
          </p>
        </div>
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-white/50 hover:text-red-400 hover:bg-red-500/10 transition-all"
        >
          <LogOut className="w-4 h-4" />
          Deconnexion
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-[#050505]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-[#050505] border-r border-white/5 fixed h-full z-30">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSidebarOpen(false)} />
          <aside className="relative flex flex-col w-64 h-full bg-[#050505] border-r border-white/5">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen">
        {/* Mobile header */}
        <header className="lg:hidden flex items-center justify-between p-4 border-b border-white/5 bg-[#050505]">
          <button
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-button"
            className="text-white/60 hover:text-white"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="ViralSynth" className="w-36 h-auto" />
          </div>
          <div className="w-6" />
        </header>

        <main className="flex-1 overflow-y-auto p-6 md:p-8 lg:p-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
